import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import ProjectData from "./ProjectData";
import ProjectionAssignation from "./ProjectionAssignation";
import ProjectionPeople from "./ProjectionPeople";

export default class Projection {
  externalId: string;
  projects: ProjectData[];
  people: ProjectionPeople[];

  constructor(externalId: string = "", projects: ProjectData[] = [], people: ProjectionPeople[] = []) {
    this.externalId = externalId;
    this.projects = projects;
    this.people = people;
  }

  findPersonByExternalId(externalId: string) {
    return this.people.find((p) => p.userExternalId === externalId);
  }

  getUsersAvailable(): ProjectionPeople[] {
    return this.people.filter((p) => p.availableHours > 0);
  }

  getUsersNamesPerProject(project: ProjectData): AutoCompleteUIDTO[] {
    const assignations = project.assignations;
    const projectUsers: AutoCompleteUIDTO[] = assignations.map((a) => {
      return { label: `${a.lastName} ${a.firstName}`, externalId: `${a.userExternalId}` };
    });
    return projectUsers;
  }

  getFullBilledUsers(): ProjectionPeople[] {
    return this.people.filter((p) => p.availableHours === 0);
  }

  getUnassignedUsers(): ProjectionPeople[] {
    return this.people.filter((p) => p.assignations.length === 0);
  }

  getFullBilledAssignationsInProject(projectName: string): ProjectionAssignation[] {
    const project = this.projects.find((p) => p.projectName === projectName);
    return project ? project?.assignations.filter((a) => a.isFullBilled()) : [];
  }

  removeAssignation(assignation: ProjectionAssignation) {
    this.projects.forEach((proj) => {
      if (proj.projectName === assignation.projectName) {
        proj.assignations = proj.assignations.filter((a) => a.externalId !== assignation.externalId);
      }
    });

    this.people.forEach((p) => {
      if (p.userExternalId === assignation.userExternalId) {
        p.assignations = p.assignations.filter((a) => a.externalId !== assignation.externalId);
        p.availableHours = p.freeHours();
      }
    });
  }

  addAssignation(assig: ProjectionAssignation, index?: number) {
    this.projects.forEach((proj) => {
      if (proj.projectName === assig.projectName) {
        proj.assignations.splice(index ? index : proj.assignations.length, 0, assig);
      }
    });

    this.people.forEach((p) => {
      if (p.userExternalId === assig.userExternalId) {
        p.assignations.find((a) => a.externalId === assig.externalId) === undefined && p.assignations.push(assig);
        p.availableHours = p.freeHours();
      }
    });
  }

  findPersonMaxHoursByAssignation(assig: ProjectionAssignation): number {
    const person = this.people.find((p) => p.userExternalId === assig.userExternalId);
    //Check if the person exist and then set the maximum possible hours for the user.
    return person !== undefined ? (assig.externalHours + person.availableHours < 8 ? assig.externalHours + person.availableHours : 8) : 0;
  }

  updateProjectionAssignation(assignation: ProjectionAssignation, role?: string, hours?: number) {
    //Updates the assignation in the project
    this.projects.find((p) => p.projectName === assignation.projectName)!.updateAssignationData(assignation.externalId, role, hours);
    //Updates the assignation in the user
    this.people.find((p) => p.userExternalId === assignation.userExternalId)!.updateAssignationData(assignation.externalId, role, hours);
  }

  changeAssignationOwner(
    oldAssignation: ProjectionAssignation,
    newUserData: AutoCompleteUIDTO,
    index: number
  ): ProjectionAssignation | undefined {
    this.removeAssignation(oldAssignation);
    const newUser = this.people.find((p) => p.userExternalId === newUserData.externalId);
    if (newUser) {
      let newAssignation = new ProjectionAssignation(
        oldAssignation.projectName,
        newUser.firstName,
        newUser.lastName,
        newUser.userExternalId,
        "",
        0,
        0,
        false,
        oldAssignation.externalId
      );
      this.addAssignation(newAssignation, index);
      return newAssignation;
    }
    return undefined;
  }

  getDefaultProjectName(): string {
    return this.projects !== undefined && this.projects.length > 1 ? this.projects[1].projectName : "Platform";
  }

  newAssignationData(newUserId: string, assignation: ProjectionAssignation): ProjectionAssignation {
    const newPerson = this.people.find((p) => p.userExternalId === newUserId);
    if (newPerson) {
      assignation.userExternalId = newUserId;
      assignation.firstName = newPerson.firstName;
      assignation.lastName = newPerson.lastName;
      assignation.externalHours = newPerson.freeHours();
      this.addAssignation(assignation);
    }

    return assignation;
  }
}
